import { useNavigate, Link } from 'react-router-dom';
import { Zap, Target, TrendingUp, Award, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TestimonialsSection from '@/components/TestimonialsSection';

function LandingPage() {
  const navigate = useNavigate();

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/onboarding';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 w-full z-50 glass-effect">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Zap className="text-green-500 w-8 h-8" />
            <span className="font-['Outfit'] text-2xl font-bold">Negotiation Ninja AI</span>
          </div>
          <Button 
            onClick={handleLogin}
            className="bg-green-600 hover:bg-green-500 text-white font-bold shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-all hover:scale-105"
            data-testid="login-button"
          >
            Get Started Free
          </Button>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-block px-4 py-2 bg-green-500/20 border border-green-500/40 rounded-full text-green-400 text-sm font-semibold mb-6">
              <Sparkles className="inline w-4 h-4 mr-2" />
              AI-Powered Negotiation Coach
            </div>
            <h1 className="font-['Outfit'] text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Master the Art of <span className="text-green-500">Negotiation</span> with AI
            </h1>
            <p className="text-gray-400 text-lg mb-8">
              From salary raises to bill reductions, car purchases to freelance rates. Practice, learn, and win every negotiation with our AI-powered coach.
            </p>
            <Button 
              onClick={handleLogin}
              size="lg"
              className="bg-green-600 hover:bg-green-500 text-white font-bold text-lg px-8 py-6 shadow-[0_0_20px_rgba(34,197,94,0.5)] transition-all hover:scale-105"
              data-testid="hero-cta-button"
            >
              Start Training Free
            </Button>
            <p className="text-sm text-gray-500 mt-4">No credit card required • 3 free roleplays</p>
          </div>
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1675025382844-0b2fe4d87e7f?w=800" 
              alt="Ninja Abstract"
              className="rounded-2xl border border-white/10 shadow-[0_0_40px_rgba(34,197,94,0.2)]"
            />
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gradient-to-b from-black/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Outfit'] text-4xl font-bold text-center mb-12">Why <span className="text-green-500">Negotiation Ninjas</span> Win</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="ninja-card">
              <Target className="text-green-500 w-12 h-12 mb-4" />
              <h3 className="font-['Outfit'] text-xl font-bold mb-3">AI Roleplay Simulator</h3>
              <p className="text-gray-400">Practice real negotiations with AI opponents. Get instant feedback and coaching tips.</p>
            </div>
            <div className="ninja-card">
              <TrendingUp className="text-green-500 w-12 h-12 mb-4" />
              <h3 className="font-['Outfit'] text-xl font-bold mb-3">Custom Scripts</h3>
              <p className="text-gray-400">Generate personalized negotiation scripts for any scenario with win probability estimates.</p>
            </div>
            <div className="ninja-card">
              <Award className="text-green-500 w-12 h-12 mb-4" />
              <h3 className="font-['Outfit'] text-xl font-bold mb-3">Daily Challenges</h3>
              <p className="text-gray-400">Gamified tasks to keep you sharp. Track your wins and savings over time.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-['Outfit'] text-4xl font-bold text-center mb-12">Simple, Transparent Pricing</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="ninja-card">
              <h3 className="font-['Outfit'] text-2xl font-bold mb-2">Free</h3>
              <div className="text-4xl font-bold text-green-500 mb-6">£0</div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> 3 AI roleplays per month</li>
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> Basic script generation</li>
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> Daily challenges</li>
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> Win tracking</li>
              </ul>
              <Button 
                onClick={handleLogin}
                variant="outline" 
                className="w-full border-white/20 hover:bg-white/10"
                data-testid="free-plan-button"
              >
                Start Free
              </Button>
            </div>
            <div className="ninja-card border-green-500/50 relative">
              <div className="absolute -top-3 right-4 bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-bold">POPULAR</div>
              <h3 className="font-['Outfit'] text-2xl font-bold mb-2">Premium</h3>
              <div className="text-4xl font-bold text-green-500 mb-1">£7.99<span className="text-lg text-gray-400">/mo</span></div>
              <p className="text-sm text-gray-400 mb-6">or £79/year (save 17%)</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> <strong>Unlimited</strong> AI roleplays</li>
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> Advanced script analytics</li>
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> Export scripts to PDF</li>
                <li className="flex items-center gap-2"><Check className="text-green-500 w-5 h-5" /> Priority support</li>
              </ul>
              <Button 
                onClick={handleLogin}
                className="w-full bg-green-600 hover:bg-green-500 text-white font-bold shadow-[0_0_15px_rgba(34,197,94,0.4)] transition-all hover:scale-105"
                data-testid="premium-plan-button"
              >
                Start 3-Day Free Trial
              </Button>
            </div>
          </div>
        </div>
      </section>

      <TestimonialsSection />

      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500">&copy; 2026 Negotiation Ninja AI. Master the Art of Negotiation.</p>
            <div className="flex gap-6 text-sm">
              <Link to="/terms" className="text-gray-400 hover:text-green-400 transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy" className="text-gray-400 hover:text-green-400 transition-colors">
                Privacy Policy
              </Link>
              <a href="mailto:guniboparai@gmail.com" className="text-gray-400 hover:text-green-400 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;